/**
 * PreviewView.tsx - PreviewView - Universal data preview and format conversion
 * 
 * CHANGE HISTORY:
 * - 251021_0641 [a6c7876] danielbeer: docs(inspectr): complete upgrade documentation
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { TranslatrManager } from '@opcentral/translatr';
import { useDraggable, useFileExport } from '@opcentral/draggr';
import PreviewViewToolbar from './PreviewViewToolbar';
import PreviewViewContent from './PreviewViewContent';
import './PreviewView.css';

export interface PreviewViewProps {
  /** Data to preview */
  data: unknown;
  /** Initial format */
  initialFormat?: string;
  /** Available formats */
  formats?: string[];
  /** Enable clipboard operations */
  enableClipboard?: boolean;
  /** Enable download */
  enableDownload?: boolean;
  /** Enable drag & drop */
  enableDrag?: boolean;
  /** Enable search */
  enableSearch?: boolean;
  /** Show statistics */
  showStats?: boolean;
  /** Enable syntax highlighting */
  syntaxHighlight?: boolean;
  /** Theme */
  theme?: 'light' | 'dark';
  /** Max height */
  maxHeight?: string;
  /** Filename for exports */
  filename?: string;
  /** Format change callback */
  onFormatChange?: (format: string) => void;
  /** Export callback */
  onExport?: (format: string, data: unknown) => void;
}

/**
 * PreviewView Component
 */
export const PreviewView: React.FC<PreviewViewProps> = ({
  data,
  initialFormat = 'json',
  formats = ['json', 'csv', 'tsv', 'xml', 'markdown', 'text'],
  enableClipboard = true,
  enableDownload = true,
  enableDrag = true,
  enableSearch = false,
  showStats = true,
  syntaxHighlight = true,
  theme = 'light',
  maxHeight = '600px',
  filename = 'export',
  onFormatChange,
  onExport
}) => {
  // State
  const [currentFormat, setCurrentFormat] = useState(initialFormat);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [formattedData, setFormattedData] = useState('');
  
  // Translatr instance
  const translatr = useMemo(() => new TranslatrManager(), []);

  // Format data (async conversion)
  useEffect(() => {
    const formatData = async () => {
      if (!data) {
        setFormattedData('');
        return;
      }

      try {
        switch (currentFormat) {
          case 'json':
            setFormattedData(JSON.stringify(data, null, 2));
            break;
          case 'csv': {
            const result = await translatr.convert(data, 'json', 'csv');
            setFormattedData(result.success ? result.data : JSON.stringify(data, null, 2));
            break;
          }
          case 'tsv': {
            const result = await translatr.convert(data, 'json', 'csv', { delimiter: '\t' });
            setFormattedData(result.success ? result.data : JSON.stringify(data, null, 2));
            break;
          }
          case 'xml': {
            const result = await translatr.convert(data, 'json', 'xml');
            setFormattedData(result.success ? result.data : JSON.stringify(data, null, 2));
            break;
          }
          case 'markdown': {
            const result = await translatr.convert(data, 'json', 'markdown');
            setFormattedData(result.success ? result.data : JSON.stringify(data, null, 2));
            break;
          }
          case 'text':
            setFormattedData(typeof data === 'string' ? data : JSON.stringify(data, null, 2));
            break;
          default:
            setFormattedData(JSON.stringify(data, null, 2));
        }
      } catch (error) {
        setFormattedData(`Error formatting data: ${(error as Error).message}`);
      }
    };

    formatData();
  }, [data, currentFormat, translatr]);

  // Statistics
  const stats = useMemo(() => {
    const bytes = new Blob([formattedData]).size;
    let size: string;
    
    if (bytes < 1024) {
      size = `${bytes} B`;
    } else if (bytes < 1024 * 1024) {
      size = `${(bytes / 1024).toFixed(1)} KB`;
    } else {
      size = `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    }

    let itemCount = 0;
    if (Array.isArray(data)) {
      itemCount = data.length;
    } else if (typeof data === 'object' && data !== null) {
      itemCount = Object.keys(data).length;
    } else {
      itemCount = 1;
    }

    return {
      itemCount,
      size,
      bytes,
      format: currentFormat,
      lines: formattedData.split('\n').length
    };
  }, [data, formattedData, currentFormat]);

  // Draggr integration
  const { dragRef, isDragging } = useDraggable({
    type: 'preview-data',
    data: {
      content: formattedData,
      format: currentFormat,
      filename: `${filename}.${getFileExtension(currentFormat)}`
    },
    enabled: enableDrag
  });

  // File export integration
  const { exportAsFile } = useFileExport();

  // Handle format change
  const handleFormatChange = useCallback((format: string) => {
    setCurrentFormat(format);
    if (onFormatChange) {
      onFormatChange(format);
    }
  }, [onFormatChange]);

  // Handle copy to clipboard
  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(formattedData);
      // TODO: Show success notification via Notifyr
    } catch (error) {
      console.error('Failed to copy:', error);
      // TODO: Show error notification via Notifyr
    }
  }, [formattedData]);

  // Handle download
  const handleDownload = useCallback(() => {
    const extension = getFileExtension(currentFormat);
    const mimeType = getMimeType(currentFormat);
    const exportFilename = `${filename}.${extension}`;

    const blob = new Blob([formattedData], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = exportFilename;
    a.click();
    URL.revokeObjectURL(url);

    if (onExport) {
      onExport(currentFormat, data);
    }
  }, [formattedData, currentFormat, filename, data, onExport]);

  // Handle drag export
  const handleDragExport = useCallback(() => {
    if (!enableDrag) return;

    const extension = getFileExtension(currentFormat);
    const mimeType = getMimeType(currentFormat);
    const exportFilename = `${filename}.${extension}`;

    exportAsFile(formattedData, exportFilename, mimeType);
  }, [formattedData, currentFormat, filename, enableDrag, exportAsFile]);

  // Toggle search
  const handleToggleSearch = useCallback(() => {
    setShowSearch(!showSearch);
    if (showSearch) {
      setSearchQuery('');
    }
  }, [showSearch]);

  return (
    <div 
      className={`preview-view preview-view-theme-${theme} ${isDragging ? 'preview-view-dragging' : ''}`}
      ref={enableDrag ? dragRef : undefined}
    >
      <PreviewViewToolbar
        currentFormat={currentFormat}
        formats={formats}
        stats={showStats ? stats : undefined}
        enableClipboard={enableClipboard}
        enableDownload={enableDownload}
        enableSearch={enableSearch}
        enableDrag={enableDrag}
        showSearch={showSearch}
        searchQuery={searchQuery}
        onFormatChange={handleFormatChange}
        onCopy={handleCopy}
        onDownload={handleDownload}
        onToggleSearch={handleToggleSearch}
        onSearchChange={setSearchQuery}
        onDragExport={handleDragExport}
      />

      <PreviewViewContent
        content={formattedData}
        format={currentFormat}
        searchQuery={searchQuery}
        syntaxHighlight={syntaxHighlight}
        maxHeight={maxHeight}
        theme={theme}
      />

      {isDragging && (
        <div className="preview-view-drag-overlay">
          <div className="preview-view-drag-message">
            📄 Drag to export as {currentFormat.toUpperCase()}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Get file extension for format
 */
function getFileExtension(format: string): string {
  const extensions: Record<string, string> = {
    json: 'json',
    csv: 'csv',
    tsv: 'tsv',
    xml: 'xml',
    markdown: 'md',
    text: 'txt'
  };
  return extensions[format] || 'txt';
}

/**
 * Get MIME type for format
 */
function getMimeType(format: string): string {
  const mimeTypes: Record<string, string> = {
    json: 'application/json',
    csv: 'text/csv',
    tsv: 'text/tab-separated-values',
    xml: 'application/xml',
    markdown: 'text/markdown',
    text: 'text/plain'
  };
  return mimeTypes[format] || 'text/plain';
}

export default PreviewView;
